import { MessageRole } from '../../clients/IsaConversationClient/generated/ConversationMessageClient/types';
import { validateProps } from '../../utils';
import { isValidBase } from '../BaseEntity';
export { MessageRole };

/**
 * Domain entity for a wix.individual_scheduling_assistant.v1.conversation_message.
 *
 * Discriminated union on `role` — mirrors the proto's role-conditional required fields:
 * - USER / ASSISTANT / SYSTEM / TOOL_RESULT / THINKING → `content` is required
 * - TOOL_USE                                           → `toolName` + `toolInput` (JSON) are required
 *
 * `metadata.toolUseId` links TOOL_USE ↔ TOOL_RESULT pairs across the conversation.
 * THINKING rows come in two kinds: a tool-authored "doing X" label for the UI, carried as `content`;
 * and the model's own reasoning, whose text and signature ride `metadata` with `content` left empty
 * (see `thinkingSignature`) so no client renders it.
 */
/**
 * Typed metadata persisted on IsaConversationMessage rows. The union of all fields — each row
 * uses only the subset that applies to its role:
 *
 * - `toolUseId`   — TOOL_USE / TOOL_RESULT / THINKING rows. Links rows of one assistant turn.
 * - `references`  — USER rows. @-mention references attached by the mobile client.
 * - `iterationId` — Per-turn ID generated at the start of each agentic-turn gateway call (currently
 *                   `Date.now().toString()`). Shared by every row the turn persists (ASSISTANT +
 *                   its TOOL_USE/THINKING rows + the TOOL_RESULT rows). orion-live groups by this.
 * - `reasoning`   — TOOL_USE rows. The LLM-stated rationale for invoking the tool, extracted from
 *                   tool input before persistence. Monitoring-only — tool implementations never
 *                   see this value, and it does not flow back to the LLM on subsequent turns.
 */
/**
 * Attachment metadata stored on USER message rows. Mobile uploads to genie-upload first, then
 * sends { fileName, mimeType, url, size } in the body of the streamed-message request.
 *
 * Two server-side policies apply:
 *
 * 1. Size — there is no upload limit of ours, for any type or size. `size` decides only how the file
 *    reaches the model: at or under `MAX_MODEL_INLINE_ATTACHMENT_BYTES` (5 MB) it is handled by type;
 *    over it, and for any format the model cannot read, the resolver maps it to a text note and its URL
 *    rides the metadata for the agent to fetch, convert or hand to a connector. `size` is
 *    optional because older mobile builds predate the field; when it's missing,
 *    `classifyGatewayError` (in streaming-agentic-turn) is the fallback — it sniffs known
 *    size-rejection markers from provider error messages and translates the SSE event into
 *    `AssistantStreamErrorCode.ATTACHMENT_TOO_LARGE`.
 *
 * 2. History stripping — `entitiesToGatewayMessages` emits media blocks only for USER rows
 *    not yet followed by a final ASSISTANT row. Once the assistant finalizes a reply to a
 *    turn, that turn's media drops from subsequent gateway calls (the model already "saw" it
 *    in its prior response — re-sending bytes on every iteration would just bloat the
 *    request). Persistence is untouched; this only filters LLM-facing emission.
 *    ASSISTANT_STEP doesn't count as "answered" so mid-tool-loop iterations still see the
 *    open turn's media.
 */

/**
 * Per-iteration LLM spend. `microcents` is in 1e-8 USD. The token split carries the per-call
 * cost drivers: `inputTokens` bill at full rate, `cacheReadTokens` at ~0.1x, `cacheCreationTokens`
 * at ~1.25x. Thinking tokens are not broken out separately — with adaptive thinking on they are billed
 * inside `outputTokens`, so a reasoning turn shows up there rather than in a field of its own.
 */

/**
 * Marker persisted on the synthetic ASSISTANT row that announces a newly-hired agent (see
 * ManageAgent `create`). Clients that recognize it render the row as the branded "agent joined"
 * orbit card (the same genome-assemble motion as the Agents hero / connecting splash), which
 * self-animates on mount; the row's plain-text `content` is the graceful fallback for any client
 * that doesn't. The row is stripped from the LLM-facing view (see gatewayMapping) so the model
 * still performs its own mandated "meet {name}" introduction.
 */

/**
 * Marker persisted on the synthetic ASSISTANT row that announces an agent has STARTED WORK on a task
 * (see ManageAgent `spawn`). Clients that recognize it render the row as the "agent at work" card: the
 * agent's node animating, the task's live status and current action, the sub-agent's conversation
 * streaming in as it works, and what the task has cost so far.
 *
 * Sibling of {@link AgentCardMetadata}, and deliberately shaped differently. The join card is a
 * SNAPSHOT — the hire happened, and the row carries everything the card will ever show. This card is a
 * WINDOW onto work still in motion, so **the only thing it needs on the row is `taskId`**. Everything
 * else is read live off that task: its status, the action sentence, the credit total, which conversation
 * the worker is running (`agentConversationId`) and which the reviewer is (`reviewerConversationId`),
 * and even the titles.
 *
 * That is a deliberate reduction. A persisted row cannot be updated as the work proceeds without
 * rewriting history, which is forbidden (see the prompt-cache rules) and would invalidate every cached
 * token behind it on every step the agent takes. So any value ALSO written here is a value that can go
 * stale and then contradict the live card beside it — the task gets restated, the reviewer changes, the
 * work moves to a second agent. `taskId` is the one identifier that cannot go stale.
 *
 * The remaining fields are FIRST-FRAME HINTS only, never a source of truth: they let the card render
 * something correct in the moment between mounting and its first read landing, and they are what it
 * falls back to if that read fails. A reader must always prefer the live task where it has one.
 *
 * The row is stripped from the LLM-facing view (see {@link isPresentationCardRow}) so the model still
 * says in its own words that the work has started — the tool result already tells it to.
 */

/**
 * Which setup milestone a {@link MilestoneCardMetadata} celebrates — drives the client's copy and,
 * for `plan`, its icon + eyebrow label. `workspace`/`dashboard`/`scan` render the Figma bubble shell
 * with fixed localized copy per kind; `plan` keeps the compact badge+name layout.
 */

/**
 * Present only on a synthetic ASSISTANT row that celebrates a setup milestone — a workspace being
 * created, the home dashboard being built, or the first plan/routine going live. Its presence is the
 * client's signal to render a milestone card (a lighter sibling of the agent-join orbit, see
 * {@link AgentCardMetadata}). Like the agent card, the row is stripped from the LLM-facing view (see
 * gatewayMapping) so the model never treats its own celebratory marker as a message to respond to.
 * The client composes the localized label from `kind`; `name` / `subtitle` fill in the specifics.
 */

/** Why the agent gave credits away — drives the card's copy and its tone. */

/**
 * Present only on a synthetic ASSISTANT row announcing that the agent gave the user credits from its
 * goodwill allowance (see the GrantGoodwillCredits tool). Its presence is the client's signal to
 * render the credit-gift card — a confetti moment for `appreciation`, a quieter make-good for
 * `reimbursement`. Like the agent and milestone cards, the row is stripped from the LLM-facing view
 * so the model never reads its own celebratory marker as something to reply to.
 *
 * `credits` is the number that ACTUALLY landed, which the server may have clamped below what the
 * agent asked for. Clients render this field and nothing else — a card that congratulates the user
 * on credits they did not receive is worse than no card, and it is the exact failure a
 * separately-authored piece of copy would eventually produce.
 *
 * The two kinds share a shape but not a mood, and the difference is not decorative: confetti over an
 * apology reads as celebrating the user's bad day.
 */

/**
 * WHICH credits nudge this card is. The server decides; clients pick copy and button order from it.
 *
 * Absent means `low` — the only variant that existed when the first clients shipped, and the natives
 * ride a later release train than the server. A client that has never heard of a variant must keep
 * rendering the card it already knows rather than blanking, so this is read with a default and never
 * switched on exhaustively.
 */

/**
 * The "running low on credits" marker. Like {@link ConnectorCardMetadata} it RIDES the reply itself:
 * clients render the card UNDER the reply text, so the assistant still answers and the nudge sits
 * beneath the answer.
 *
 * Deliberately not a modal on any surface. A prompt that covers the screen at the moment something
 * worked interrupts the very thing it is congratulating; in the thread it costs nothing to scroll past.
 */

/**
 * WHICH invite ask this is. `recommend` is the account's once-ever ask (no reward wording); `earn`
 * names the referral reward. Absent or unrecognized reads as `earn` — a client meeting a newer server
 * keeps rendering the card it knows rather than blanking.
 */

/**
 * "Know someone who'd like this?" — the owner's own invite link, offered under a reply at a good
 * moment. Rides the reply like {@link CreditsCardMetadata}. The reward triple is the REFERRER reward
 * from `BalanceService.GetRewardRules`, present only on `earn`; it is served, never hard-coded, and a
 * client missing any of the three drops the numbers from its copy rather than printing a partial one.
 */

/** What {@link readInviteCard} returns: the marker with its variant always resolved. */

/** "Share this pack" — on the reply of the turn that created `packs/<slug>/pack.md`. */

/**
 * Present on an ASSISTANT reply whose turn minted a connector OAuth link (ConnectApp), on
 * card-capable channels (app/web). Clients render a connect card — the app's logo tile, name, and a
 * Connect button opening `connectUrl` — under the reply text, which deliberately carries no raw
 * connect URL. The card is LIVE: clients derive its state from their connectors data (connected →
 * success surface) and from `mintedAt` (a stale un-connected link → offer to re-mint). Text-only
 * channels (WhatsApp/SMS/email/external agents) never get this marker; the reply carries the link
 * as text there instead.
 */

/**
 * The statuses a profile link card can carry. An ARCHIVED card draws nothing: it rides the reply to an archive so the
 * clients' latest-card-wins rule retires the earlier cards for that link.
 */
export const PROFILE_LINK_CARD_STATUSES = ['DRAFT', 'ACTIVE', 'PAUSED', 'ARCHIVED'];

/**
 * Present on an ASSISTANT reply whose turn left a profile link the owner should look at — ManageProfileLinks
 * any write action (`claim`, `create`, `update`, `publish`, `pause`, `resume`, `archive`) — on card-capable channels
 * (app/web). Clients render "Your Symphony card" under the reply: the link, its headline, Open / Share once live, and
 * Edit while it is not live yet or paused. `status` is the state AFTER the turn's write; within a conversation the
 * latest card for a link decides what every earlier card for it shows. Rides the reply exactly like {@link ConnectorCardMetadata}: the TOOL_RESULT row carries it, the turn's final
 * reply is stamped with it, and `messageEnd` carries it in-band. Text-only channels never get it; the reply carries the
 * link in words there instead.
 */

/**
 * The value family an opportunity belongs to — the chip the client draws, colour + icon coded so it
 * is scannable before a word is read. The maestro picks exactly ONE per opportunity, tagging by the
 * dominant outcome the owner cares about: an opportunity serving two families (self-serve booking is
 * both `new-clients` and `time-saved`) gets the one that is the point.
 *
 * Nine business families then seven personal ones. Context narrows the choice — a business workspace
 * surfaces the business families, a personal workspace the personal ones — but the union is one flat
 * set because a workspace is not typed and the split is a matter of what fits, not of what is legal.
 * The sub-topics that define each family live in the authoring prompt, not here: code only ever needs
 * to know which ids exist, so an unrecognized one can fail closed.
 */

/**
 * What a connector's kit installed, as a card on the turn its connect lands.
 *
 * Deliberately its own card rather than a state on {@link ConnectorCardMetadata}, which offers a
 * connect that has not happened yet. The two have opposite lifecycles — one is minted by ConnectApp
 * and expires, this one describes an account that is already set up — and folding them together would
 * make one key a discriminated union every client has to branch on forever.
 *
 * The prose beside it is NOT redundant. A client that does not know this key renders the reply and
 * silently omits the card — there is no text fallback anywhere in this pipeline — so the reply has to
 * say what the connection brought on its own, and this shows it.
 *
 * <p>Every field is resolved server-side from the account's own routines, never retyped by the model.
 * The card carries actions, and a hallucinated routine is a control that does nothing.
 */

/** One agent on a band card: who they are and what they own, in the user's language. */

/** Where a band's app stands. Absent on an offer, where nothing has been connected yet. */

/** One app on a band card. `toolkit` is the key clients match against their connectors and routines. */

/** One of the bands offered: a name for the user's business, one line, the team and the apps. */

/**
 * The band offer: two whole teams for the business, side by side, for the user to pick from. Carries
 * content only. The pick is an ordinary `choices` chip on the same row, one per option plus "both", so a
 * tap comes back as a {@link ChoiceAnswer} the agent handles, exactly like the opportunity card's CTAs.
 *
 * Rides the assistant's reply like `connectorCard`; the reply's own words still name the two bands, so a
 * client that cannot draw the card still shows a usable offer.
 */

/**
 * The band, once it exists: the team that was hired and every app it runs on with where each stands.
 * Stamped on the reply that follows the band-connect chain (see the chain store in the harness), and on
 * the reply of the turn that created the band when it had no apps to connect.
 *
 * Its rows fold: a client draws one collapsed row per app, and expands it to that app's routines read
 * LIVE from its own routines store filtered by `toolkit`, the same rule as {@link PostConnectedConnectorCardMetadata}.
 * Four apps landing in one chain would otherwise stack four connector cards under one reply.
 */
/**
 * Where one of the goal's laid-out tasks stands.
 *
 * A band's goal is laid out but NOT started, so every task is `idle` on the card the user first sees —
 * that is the point of drawing them. The other two exist because the card is restamped when the connect
 * walk finishes, which can be minutes later and after the user has already said go.
 */

/** One laid-out task of the band's goal, and who runs it. */

/**
 * The outcome the band was picked for, laid out on the created card with the work that reaches it.
 *
 * The goal is started when the band is created: the user picked the band from a card that showed this
 * outcome, and that pick is their go. Each task reads `working` once its agent is on it, and `idle` when
 * the plan was held at the user's request or the start did not go through, so the card never claims
 * work that is not running.
 */

/**
 * `plan`: the goal laid out and waiting on the user's go, then running — one card that follows the goal
 * live. `completed`: the goal reached, on the reply that says so.
 */

/**
 * The goal card: a compact, LIVE view of one goal in the chat.
 *
 * The metadata is only the goal's identity plus a snapshot for first paint. Everything that moves — the
 * status, each task's state, the percent, whether the Kick off button still shows — is read by the client
 * from the live goal, found by `id` and else by `slug`. The row itself is never rewritten as the goal
 * progresses: persisted history is immutable (prompt-cache stability), and a card that restated state in
 * its metadata would go stale the moment the first task moved.
 *
 * The `plan` card rides the reply that asks for the go — the turn that carries the band card with its
 * goal. The `completed` card rides the reply to the goal service's "Goal reached" SYSTEM row, which the
 * harness reads the goal's title and slug from (`collectActiveTurnGoalCard`).
 *
 * Kick off only SENDS a chat message. Starting the goal stays the maestro's move, exactly as when the user
 * types "yes", so a client that cannot draw this card loses the picture and keeps the feature.
 */

/** The channel a previewed outreach travels on. The card is skinned by it: colour, glyph and frame. */

/**
 * How the recipient meets the words. `verbatim`: exactly as written. `wrapped`: inside the fixed
 * WhatsApp intro template, which is what a first contact or a lapsed window reads. `contextual`: a
 * fixed SMS intro goes first and the words follow once the recipient replies.
 */

/** One recipient on the preview card: who, where, and the message as it lands for them. */

/** Everyone one channel reaches in this approval; the client draws a group as one row of cards. */

/**
 * The outreach preview over chat: what each contact will receive, drawn for the channel it travels on,
 * emitted by the outreach tool at its approval gate instead of being typed by the model.
 *
 * Every string on it is final and already localized by the server, so the three clients render it with
 * no keys of their own: the labels are what the card captions its fields with, and `actions` are the
 * chip labels the harness mints into `choices` on the same reply, so a tap comes back as an ordinary
 * {@link ChoiceAnswer} the agent handles. Rides the reply like `connectorCard`, and for the same reason
 * stays out of {@link isPresentationCardRow}: the reply's own words are the lead-in.
 */

export const SOCIAL_POST_TYPES = ['post', 'reel', 'story', 'video', 'short', 'pin', 'carousel'];
export const MAX_SOCIAL_POST_PREVIEW_MEDIA = 10;

/** The kinds a Share Card ships in this phase. `PLAN`/`DASHBOARD` are in the policy table, still off. */

/**
 * The channels a Share Card can go out on. Deliberately only two: SMS awaits its consent review and
 * `GMAIL_CONNECTOR` has no reply-ingestion path at all, so a card whose whole value is the reaction
 * coming back would look dead on it.
 */

/**
 * Where one name chip stands. `ready` is the untapped chip; `sending` is the ten-second hold, the only
 * state that offers Undo; `sent` is the settled state after it. The last three are failures said
 * plainly rather than rounded: `undone` took it back, `undo_refused` means the card had ALREADY gone
 * when Undo was asked (the service refused, so the client must not draw it as undone), and `failed`
 * means it never went out at all.
 */

/** One option on a `CHOICE` card — what the recipient picks between. */

/** The thing being shown on a `FILE` card, already public: the page fetches nothing and rehosts nothing. */

/**
 * One name on the offer, and WHERE ITS SEND STANDS.
 *
 * ⚠️ This is not a control. The tappable name is the reply's own choice chip (see
 * {@link shareSendOptionId}) — a card renders no buttons, so that an app that has never heard of this
 * key can still send. What the card draws from a chip is its STATE: a `ready` chip is drawn not at all,
 * because the choice chip below already is it, and drawing both would offer the same send twice.
 *
 * **Tapping the name SENDS** — no confirm step, no follow-up question (design §7.1) — which is why a
 * chip exists only for a person who is already send-ready, and why the only thing the tap carries is
 * the person id inside the option id. The client never names an address, a body or a channel: the
 * offer id on the ask binds all three server-side.
 */

/**
 * The share offer over chat: the card exactly as the recipient will meet it, plus one to three name
 * chips under it.
 *
 * Every string on it is final and already localized by the server — the same rule
 * {@link OutreachPreviewCardMetadata} follows, and for the same reason: three clients that share no
 * translation infrastructure with the harness draw it, so the harness writes the words once.
 *
 * ⚠️ **The reply this rides must carry prose, `choices`, or both.** All three clients drop a
 * content-less assistant row with nothing else on it, so an offer whose whole substance was this
 * metadata is dropped on EVERY build, new ones included — the card is additive, and a row cannot be
 * made of additions alone.
 *
 * ⚠️ **The card renders no buttons.** Its names are the reply's ordinary {@link MessageChoices}, minted
 * from the same offer (see {@link shareSendOptionId}), exactly as the outreach preview's Send/Edit are
 * — so a build that has never heard of `shareOfferCard` still shows working names and still sends. The
 * card is additive: it is the picture that build cannot draw, never the path the send travels.
 *
 * ⚠️ `undoSeconds` is **presentation**. The service holds the send and refuses a late Undo with
 * `SEND_TOO_EARLY`/`NOT_HELD`; a client timer that disagrees with it must lose, which is what
 * `undo_refused` exists to render honestly. Undo is a choice chip too, so an old build can take a card
 * back even though it draws no countdown.
 */

/** What came back from the recipient. A bare `reaction` is a templated row with NO agent turn (§8). */

/**
 * A recipient reacted, and the sender sees it in the conversation the card was sent from.
 *
 * For a bare emoji this row is the WHOLE response — design §8 makes it a templated row and no LLM
 * turn, which is why `line` is a finished sentence rather than something a model composes. A poll
 * answer, chip or written reply also wakes the agent for one free turn; this row still carries the
 * answer so the card reads correctly on reload, before or without that turn.
 */

/**
 * The opportunity widget over chat — an OPPORTUNITY-status goal put in front of the owner at a
 * moment that earns it (day 0, after the agents' morning meeting, or when a goal completes), rather
 * than left behind the Workspace tab.
 *
 * Three parts, matching how a goal is structured: the {@link ChipFamily} chip (the value family),
 * the win (the value statement, which IS the goal's title), and the how (the mechanism the agent
 * tasks deliver). `slug` is what a CTA resolves back to.
 *
 * Rides the assistant's reply like `connectorCard` and `choices` rather than being a synthetic
 * presentation row: the conversational lead-in is the model's own text, so this must stay out of
 * {@link isPresentationCardRow} or stripping the row would take the lead-in with it. The two CTAs
 * ("Set it up" / "Not now") are ordinary `choices` on the same row — this card carries content, not
 * intent.
 */

/** The kinds the insight-card clients support. Unknown kinds retain their text fallback. */
export const KNOWN_INSIGHT_KINDS = ['carts', 'abandonedBookings', 'unpaidBookings', 'overdueInvoices', 'planRenewals', 'reviews', 'sessions', 'forms', 'outOfStock', 'salesTrend', 'emailOpens', 'mailingList', 'productSales', 'gbpConnection', 'campaignClickers', 'restockDemand', 'expiringLoyaltyPoints', 'rebookingFollowUp'];
export const MAX_INSIGHT_CARD_ITEMS = 3;

/**
 * Measured numbers about the user's own site, as a card under the assistant's reply. Every number is
 * copied by the server from the received `site-insights.json`; the model names items by id only. The
 * card is a snapshot as of `computedAt` and never changes after it is posted.
 *
 * The row's own `content` must still carry the numbers in words: a client that does not know this key
 * shows only the content.
 */

export { readConnectorResultCard } from './connector-result-card';

/**
 * Product cards under a shopping reply (docs/2026-10-14-product-search-images-design.md, Phase 2). One item per
 * product picture the reply attached, in attachment order. `attachmentUrl` is that attachment's `url`, exactly: a
 * client draws the card in place of the picture with that url. The pictures still ride the row as attachments, so
 * a client that does not know this key shows them as before.
 */

/**
 * Quoted-reply reference persisted on a row whose sender replied to another message. At least one
 * of the two ids is present: a WhatsApp-originated reply always carries `waMessageId` (the target's
 * wamid; `messageId`/`preview`/`sender` fill in when the target resolved to a row in our
 * conversation), while an in-app reply carries `messageId` directly (no wamid exists). Targets with
 * neither a preview nor a resolvable row still render a generic "replied to a message" quote.
 */

/**
 * Structured ask stamped on an ASSISTANT row that offers the user an enumerable set of options
 * (the OfferChoices tool). Clients render `options` as tappable choice chips under the message;
 * tapping one sends a reply carrying a {@link ChoiceAnswer} that echoes `askId` and the tapped
 * option's `id`. The chips are an input affordance, not a constraint — the user can always answer
 * in free text instead.
 */

/**
 * The user's tap on a choice chip, stamped on the USER reply row. `askId` names the
 * {@link MessageChoices} ask being answered; `optionId` is the tapped option's `id`.
 */

/** A person the user chose themselves. At least one of `email` / `phone`; `name` is display only. */

/**
 * One selectable option inside a {@link SurveyQuestion}. Richer than a choice chip: `label` is the
 * bold row title and `description` an optional muted sub-line beneath it (per the survey-card design).
 */

/**
 * One question of a {@link MessageSurvey}. Each question is choice-shaped but stands alone: it carries
 * its own `prompt` (rendered as the survey card's header for that page), its own options, and its own
 * selection mode. `multiSelect` picks checkboxes over radios; `allowFreeText` shows an inline free-text
 * field that coexists with the options — the user may select options AND type, the typed value landing
 * in {@link QuestionAnswer.freeText} independently of `optionIds` (it is not a pseudo-option).
 */

/**
 * Structured multi-question ask stamped on an ASSISTANT row (the OfferSurvey tool). Clients render it
 * as a paginated survey card below the reply — one question per page — and answering it sends a single
 * reply carrying a {@link SurveyAnswer} that echoes `askId`. Distinct from {@link MessageChoices}: a
 * survey holds two or more questions (a single question uses OfferChoices/`choices` instead), its
 * options carry descriptions, and each question can be single- or multi-select with an optional
 * free-text answer. Like the chips, it is an input affordance the user can skip.
 */

/**
 * The user's answer to one {@link SurveyQuestion}, carried inside a {@link SurveyAnswer}. `questionId`
 * names the question; `optionIds` are the chosen options' ids (one for single-select, any number for
 * multi-select, empty when nothing was picked); `freeText` is the typed free-text when the question's
 * free-text field was filled. The two are independent — a question may come back with both selected
 * options and typed text.
 */

/**
 * The user's submitted survey, stamped on the USER reply row. `askId` names the {@link MessageSurvey}
 * ask being answered; `answers` holds one {@link QuestionAnswer} per answered question. A skipped or
 * dismissed survey sends no `surveyAnswer` at all (the same way an ignored choice chip sends none).
 */

/**
 * One reaction on a message. `by` identifies the reactor: the literal 'user' (the site owner in
 * their own chat), an agent id (agent-authored reaction), or a WhatsApp member's waId (group
 * reactions arriving via the channels pipeline). One entry per (emoji, by) — re-reacting with the
 * same emoji toggles the entry off. Stored under `metadata.reactions`, the ONLY path the
 * conversation-message service's UpdateConversationMessage accepts (annotation-only contract).
 */

/**
 * What arrived when an inbound message could not be turned into anything readable. `reason`
 * separates "nothing was in it" (a forward or interactive tap) from the media hops that failed on
 * our side — the distinction the user-facing copy and any triage both depend on.
 */

/**
 * Cap on {@link IsaConversationMessageMetadata.systemErrorReason}.
 *
 * Generous on purpose: a truncated cause is often a useless one. Provider errors bury the part that
 * identifies the failure behind a preamble, and a gRPC status line with a nested upstream message
 * runs long before it says anything specific — cutting at a few hundred characters keeps the
 * boilerplate and drops the answer, which is the failure mode this field exists to end.
 *
 * The cost of the larger cap is bounded: the key is in METADATA_OMIT_FROM_LLM, so it never reaches
 * a prompt and never costs a token no matter how long it runs. It rides one row per failed turn —
 * rare by construction — read by monitoring rather than by the agentic loop.
 */
export const SYSTEM_ERROR_REASON_MAX_LENGTH = 3000;

/**
 * The roles a transcript read asks for — the SAME five the phones' own query asks for today, so a client
 * switched onto a server route sees exactly the rows it saw before.
 *
 * TOOL_RESULT is the deliberate omission and the expensive one: exactly one per tool call, its content
 * required, carrying raw tool output (a file body, a contact list, search results). Fetching it spends a
 * fixed-size page on rows no builder renders — and because these reads sort newest-first, the rows pushed
 * off the end are the OLDEST, so a busy transcript would silently begin partway through. An oversized page
 * throws rather than shrinking, and the phones retry a 5xx.
 *
 * Lives beside MessageRole rather than in each route — the list had already been written out four times,
 * which is the same drift the shared renderer exists to end — and typed as MessageRole[] so no caller
 * needs a cast to hand it to a query.
 */
export const AGENT_TRANSCRIPT_ROLES = [MessageRole.USER, MessageRole.ASSISTANT, MessageRole.ASSISTANT_STEP, MessageRole.THINKING, MessageRole.TOOL_USE];

/**
 * The flows that dispatch a SYSTEM row and then put the user in front of the turn it drives — see
 * {@link IsaConversationMessageMetadata.awaitedByUser}. A closed set on purpose: each member is a
 * promise that a person is on the other side of that turn, and a flow that cannot keep it must not
 * claim it or a capped account gets an unread wall on every background turn it dispatches.
 */

/**
 * Why a final ASSISTANT row exists, for the notification layer — the "purpose" of §3.1 of the
 * smart-notifications design, as the harness can state it deterministically from the turn's own rows
 * (design §4.1). Stamped on every final ASSISTANT row the harness writes; a row without one is treated
 * downstream as `progress` (never "push as today"), so a writer that forgets to stamp fails quiet.
 *
 * - `needs_you` — the turn raised a Decision, parked a task ACTION_NEEDED, or relayed an inbound outreach
 *   reply: the user has to act.
 * - `result` — an answer to something the user asked for: a reply to their message, a child report the
 *   user was waiting on, a brief.
 * - `failure` — work that did not land: a task set FAILED, a credit wall.
 * - `progress` — everything else a SYSTEM-driven turn writes (an unprompted report, a nudge reply, a
 *   compaction), and every delivery mirror row (a WhatsApp template, an email) — the row exists, nobody
 *   needs to be interrupted for it.
 *
 * Derived by `collectActiveTurnIntent` in the harness; consumed by the conversation-message push producer
 * behind its own gate. Metadata only: stamping it changes no push behaviour by itself.
 */
export const NOTIFY_INTENTS = ['progress', 'result', 'needs_you', 'failure'];
export function isNotifyIntent(value) {
  return typeof value === 'string' && NOTIFY_INTENTS.includes(value);
}

/**
 * Which party's assistant a communication turn wakes (Symphony Profile Links design §11): the RECIPIENT of an
 * outreach reading a new message or an addition, or its SENDER reading the contact's reply.
 */
export const COMMUNICATION_SIDES = ['recipient', 'sender'];

/**
 * The structured marker a producer stamps on the wake that carries ANOTHER party's message into an outreach
 * conversation: the outreach service's EMAIL/APP delivery and reply relay, and digest-engine's WhatsApp/SMS
 * delivery. Classified at the wake, never inferred from the note's wording.
 *
 * `text` is the inbound message as its author wrote it (attachments named after it), so a turn that cannot run
 * can still hand it on verbatim; `authorName` is who wrote it, when the producer knows.
 */

/** Why a message was handed on verbatim instead of answered by the model. */

/**
 * The purpose a reserved interruption was granted for (design §3.1 / §4.5): the sender reserves a slot
 * under one of these, and the SYSTEM row that drives the composing turn carries it beside
 * `reservationId`, so the reply's `notifyIntent` is the reservation's purpose rather than a guess from
 * the rows. (Proto value 7, `NUDGE_PEOPLE`, belonged to the brief's removed "Coming up" producer: nothing
 * reserves under it, so it is not a purpose here — people-relationships plan §4, decided 2026-10-16.)
 * `NUDGE_CHECKIN` is the life timeline's proactive check-in (timeline P3.4), reserved by the harness's
 * own `lifeCheckIn` capsule. `CHASE` is the chaser's re-delivery of one finding an absent owner has not
 * seen (docs/2026-10-23-chaser-design.md), reserved by the recipient service's `chaser-tick`.
 */
export const NOTIFY_PURPOSES = ['NEEDS_YOU', 'DONE', 'BROKE', 'NUDGE_MARKETING', 'NUDGE_FOLLOWUP', 'NUDGE_CHECKIN', 'CHASE'];

/**
 * The header facts of a video meeting's write-up row (`metadata.meetingNotesCard`). Everything here is
 * ALSO in the row's markdown and in the attached `notes.md`; this is the same data as numbers and lists
 * so a client can render the meeting's own time in the reader's zone and lay the names out as chips
 * without parsing prose. Names are display names people chose in the room: text, never links.
 */
/**
 * The owner-approval ask a live meeting raised (`metadata.meetingApproval`), on the ASSISTANT row that
 * tells the owner about it. The row's content is the question the owner reads; this is the same ask as
 * ids, for the clients (which answer `decisionId`) and for digest-engine (which also sends it to the
 * owner's WhatsApp and maps a YES/NO reply to it). `askerLabel` and `askedQuestion` came from the room,
 * so they are text and never instructions.
 */

/**
 * On the owner's WhatsApp reply that approved a meeting approval with an answer AND went on to Maestro
 * (`metadata.meetingApprovalReply`, riding the wire references Struct like `choiceAnswer`). The verdict is
 * already cast when the turn runs; this names it so the turn knows not to answer the question again.
 * digest-engine also stamps it on the fixed confirmation row it writes, so a redelivery of the same message
 * resumes at the hand-off instead of reading the reply again.
 */

/** See `IsaConversationMessageMetadata.meetingApprovalAnswering`. */

/**
 * Read the agent-card marker off a message's metadata, returning a validated {@link AgentCardMetadata}
 * or `null` when the row is not an agent-card row. Shared by every client renderer so the "is this an
 * agent-join card?" check stays identical across web and the native bridges.
 */
export function readAgentCard(metadata) {
  const card = metadata == null ? void 0 : metadata.agentCard;
  if (card && typeof card.name === 'string' && card.name.trim().length > 0) {
    return {
      ...(typeof card.id === 'string' && card.id.trim() ? {
        id: card.id
      } : {}),
      name: card.name,
      ...(typeof card.title === 'string' && card.title.trim() ? {
        title: card.title
      } : {})
    };
  }
  return null;
}

/**
 * Read the agent-at-work marker off a message's metadata, returning a validated
 * {@link AgentAtWorkCardMetadata} or `null` when the row is not an at-work-card row. Shared by every
 * client renderer so the "is this an agent-at-work card?" check stays identical across web and the
 * native bridges.
 *
 * `taskId` is the only field required to pass, because it is the only one the card cannot work without
 * — every other value is read off the task (see the type doc). A row missing it can only ever render an
 * empty shell that never updates, so returning null there falls back to the row's plain text, which is
 * the honest outcome. The hints are each dropped when blank rather than failing the whole read.
 */
export function readAgentAtWorkCard(metadata) {
  const card = metadata == null ? void 0 : metadata.agentAtWorkCard;
  const nonEmpty = value => typeof value === 'string' && value.trim().length > 0;
  if (card && nonEmpty(card.taskId)) {
    return {
      taskId: card.taskId,
      ...(nonEmpty(card.agentId) ? {
        agentId: card.agentId
      } : {}),
      ...(nonEmpty(card.agentName) ? {
        agentName: card.agentName
      } : {}),
      ...(nonEmpty(card.agentTitle) ? {
        agentTitle: card.agentTitle
      } : {}),
      ...(nonEmpty(card.taskTitle) ? {
        taskTitle: card.taskTitle
      } : {})
    };
  }
  return null;
}

/**
 * Read the meeting-notes marker off a message's metadata, returning a validated
 * {@link MeetingNotesCardMetadata} or `null` when the row is not a meeting write-up. Shared by every
 * client renderer so the "is this a meeting row, and what were its facts?" check stays identical.
 *
 * Fail-closed on the session id alone, because that is the one field the card cannot be drawn without.
 * Everything else is dropped rather than refused: a meeting nobody spoke in has no times, an untitled
 * room has no link, and a card that still names who attended beats no card at all. A name is a display
 * name somebody chose in a video call, so it is carried as text and never resolved against anything.
 */
export function readMeetingNotesCard(metadata) {
  const card = metadata == null ? void 0 : metadata.meetingNotesCard;
  const nonEmpty = value => typeof value === 'string' && value.trim().length > 0;
  const stamp = value => typeof value === 'number' && Number.isFinite(value);
  if (!card || !nonEmpty(card.meetingSessionId)) {
    return null;
  }
  const named = list => Array.isArray(list) ? list.filter(item => nonEmpty(item == null ? void 0 : item.name)) : [];
  return {
    meetingSessionId: card.meetingSessionId,
    ...(stamp(card.startedAt) ? {
      startedAt: card.startedAt
    } : {}),
    ...(stamp(card.endedAt) ? {
      endedAt: card.endedAt
    } : {}),
    ...(nonEmpty(card.meetingUrl) ? {
      meetingUrl: card.meetingUrl
    } : {}),
    participants: named(card.participants).map(p => ({
      name: p.name,
      isHost: p.isHost === true,
      ...(nonEmpty(p.recipientId) ? {
        recipientId: p.recipientId
      } : {})
    })),
    speakers: named(card.speakers).map(s => ({
      name: s.name,
      isAgent: s.isAgent === true
    }))
  };
}
const MILESTONE_CARD_KINDS = ['workspace', 'dashboard', 'plan', 'scan', 'phone'];

/**
 * Read the milestone-card marker off a message's metadata, returning a validated
 * {@link MilestoneCardMetadata} or `null` when the row is not a milestone-card row. Shared by every
 * client renderer so the "is this a milestone card, and of which kind?" check stays identical across
 * web and the native bridges. Guards `kind` against the known set so an unknown/forward kind renders
 * nothing rather than a broken card.
 */
export function readMilestoneCard(metadata) {
  const card = metadata == null ? void 0 : metadata.milestoneCard;
  if (!card || !MILESTONE_CARD_KINDS.includes(card.kind)) {
    return null;
  }
  return {
    kind: card.kind,
    ...(typeof card.name === 'string' && card.name.trim() ? {
      name: card.name
    } : {}),
    ...(typeof card.subtitle === 'string' && card.subtitle.trim() ? {
      subtitle: card.subtitle
    } : {}),
    ...(card.nav ? {
      nav: card.nav
    } : {})
  };
}

/**
 * Read the credits-card marker off a message's metadata. Shared by every client renderer AND by the
 * harness that stamps it, so "does this reply carry a credits card?" stays one answer.
 *
 * Fail-closed on a non-positive `cap`: a card that cannot draw a meter renders nothing rather than a
 * bar pinned at zero, which would read as "you have no credits" — the opposite of this card's message.
 * Numbers are coerced from strings because harness metadata is often a `map<string,string>`.
 */
export function readCreditsCard(metadata) {
  const card = metadata == null ? void 0 : metadata.creditsCard;
  if (!card) {
    return null;
  }
  const toInt = value => {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return Math.trunc(value);
    }
    if (typeof value === 'string' && value.trim() && Number.isFinite(Number(value))) {
      return Math.trunc(Number(value));
    }
    return null;
  };
  const cap = toInt(card.cap);
  const used = toInt(card.used);
  if (cap === null || cap <= 0 || used === null) {
    return null;
  }
  // An UNRECOGNIZED variant is dropped rather than passed through, so what this returns always holds a
  // value the renderers can switch on — and dropping it lands on the same `low` default a missing one
  // does, which is the tolerant reading a client meeting a newer server needs. A recognized `low` is
  // likewise left off: it is the default, and omitting it keeps the payload byte-identical to what
  // clients predating the field already render.
  const variant = card.variant === 'renewed' ? 'renewed' : undefined;
  return {
    used: Math.max(0, used),
    cap,
    ...(typeof card.successLine === 'string' && card.successLine.trim() ? {
      successLine: card.successLine
    } : {}),
    ...(variant ? {
      variant
    } : {})
  };
}

/** A positive whole number off harness metadata, which often stringifies numbers; anything else is null. */
function positiveIntOrNull(value) {
  const n = typeof value === 'number' ? value : typeof value === 'string' && value.trim() ? Number(value) : NaN;
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : null;
}

/**
 * Read the invite-card marker. Fail-closed on the link: no https link means no card, because a share
 * button with nothing attributable behind it credits nobody and looks identical to one that works.
 *
 * The variant always comes back RESOLVED (unknown/absent → `earn`). The reward triple is all-or-nothing:
 * reward copy names all three parts, so a card missing any one of them carries none, and renderers fall
 * back to the body without numbers. `recommend` never carries them.
 */
export function readInviteCard(metadata) {
  const card = metadata == null ? void 0 : metadata.inviteCard;
  if (!card || typeof card !== 'object') {
    return null;
  }
  const raw = card;
  const link = typeof raw.link === 'string' ? raw.link.trim() : '';
  let parsed = null;
  try {
    parsed = link ? new URL(link) : null;
  } catch {
    parsed = null;
  }
  if (!parsed || parsed.protocol !== 'https:') {
    return null;
  }
  const variant = raw.variant === 'recommend' ? 'recommend' : 'earn';
  const credits = positiveIntOrNull(raw.credits);
  const subAgentRuns = positiveIntOrNull(raw.subAgentRuns);
  const agentMeetings = positiveIntOrNull(raw.agentMeetings);
  const reward = variant === 'earn' && credits !== null && subAgentRuns !== null && agentMeetings !== null;
  return {
    link,
    variant,
    ...(reward ? {
      credits,
      subAgentRuns,
      agentMeetings
    } : {})
  };
}

/**
 * Read the pack-share-card marker. Fail-closed on the slug, which becomes a path segment: it must be a
 * single non-empty segment. A missing name falls back to the slug so the sentence still names the pack.
 */
export function readPackShareCard(metadata) {
  const card = metadata == null ? void 0 : metadata.packShareCard;
  if (!card || typeof card !== 'object') {
    return null;
  }
  const raw = card;
  const slug = typeof raw.slug === 'string' ? raw.slug.trim() : '';
  if (!slug || /[/\\?#\s]/.test(slug) || slug === '.' || slug === '..') {
    return null;
  }
  const name = typeof raw.name === 'string' && raw.name.trim() ? raw.name.trim() : slug;
  return {
    slug,
    name
  };
}

/**
 * True for a synthetic ASSISTANT row that exists only to make a client draw something — the
 * agent-join orbit, an agent at work, a setup milestone, a credit gift. These rows carry placeholder text
 * (`✦ Nia joined your team`) that is a caption, not a message: the model must never see one in its
 * history, or it treats its own marker as a turn already taken and skips the introduction / thanks /
 * apology it was supposed to write itself.
 *
 * ONE predicate, used by every provider mapping. It is a shared function rather than a repeated
 * `metadata?.agentCard || metadata?.milestoneCard` because that literal WAS repeated — and had
 * already drifted: only the gateway mapping stripped these rows, while the Anthropic, Gemini and
 * OpenAI paths passed them straight to the model. A new card kind then has to be added in one place
 * to be handled everywhere, which is the only version of this that stays true.
 */
/**
 * Every metadata key that makes a client draw a card, in one place.
 *
 * This list is the reason the transport is card-agnostic. A card used to be plumbed by name through
 * four hand-maintained allowlists — the `messageEnd` payload, its TypeScript type, each client's
 * mid-turn drop guard, and each client's republish patch — so a new kind rendered promptly only if
 * every one of them was remembered. They were not: `creditsCard` was missing from Android's patch and
 * `postConnectedConnectorCard` from iOS's, and both cards then stayed invisible until a cold reload.
 *
 * Adding a card kind means adding its key HERE and nowhere else on the wire.
 */
export const CHAT_CARD_METADATA_KEYS = ['shopifyBatchCard', 'shopifyBriefingCard', 'agentCard', 'agentAtWorkCard', 'milestoneCard', 'creditGiftCard', 'connectorCard', 'creditsCard',
// The owner's invite link at a good moment (Java harness): web `lib/invite-card.ts` and both phones' twins.
'inviteCard',
// "Share this pack" on the turn that created a pack: web `lib/pack-share-chat-card.ts` and both phones' twins.
'packShareCard', 'opportunityCard', 'insightCard', 'productCards', 'connectorResultCard', 'postConnectedConnectorCard', 'outreachPreviewCard', 'socialPostPreviewCard', 'shareOfferCard', 'shareResponseCard', 'bandOfferCard', 'bandCard', 'goalCard', 'meetingNotesCard', 'proposalsCard',
// A message the turn left WAITING (send rule): web `lib/send-hold.ts`, iOS `SendHold.swift` and Android `SendHold.kt`
// read it and draw its Cancel (row D). A build predating them ignores the key; the reply's words still say it waits.
'sendHoldCard',
// The owner's profile link with its Publish (profile links activation): read by `readProfileLinkCard`.
'profileLinkCard',
// Not a card: a flag that draws nothing. It rides this list because it has to reach every client on
// every delivery path a card does — see `bandCreated` on the metadata.
'bandCreated'];

/** The card-bearing slice of a message's metadata — the shape carried in-band on `messageEnd`. */

/**
 * Compile-time exhaustiveness: a `*Card` key added to {@link IsaConversationMessageMetadata} and not
 * registered above fails the build here rather than shipping a card no phone renders on time. The
 * naming convention IS the contract — a card field is named `<thing>Card`.
 */

const _everyCardKeyIsRegistered = true;
void _everyCardKeyIsRegistered;

/**
 * The cards stamped on a row, as a bag keyed exactly like the metadata they came from. Absent keys are
 * omitted rather than set to `undefined`, so the result spreads straight onto a wire payload.
 */
export function pickChatCardMetadata(metadata) {
  const cards = {};
  for (const key of CHAT_CARD_METADATA_KEYS) {
    const card = metadata == null ? void 0 : metadata[key];
    if (card) {
      cards[key] = card;
    }
  }
  return cards;
}

/** True when a row carries any card at all — the generic form of every per-card presence check. */
export function hasChatCardMetadata(metadata) {
  return CHAT_CARD_METADATA_KEYS.some(key => Boolean(metadata == null ? void 0 : metadata[key]));
}
export function isPresentationCardRow(row) {
  var _metadata$shopifyBatc;
  const metadata = row.metadata;
  // NEITHER share-card key belongs here, for two different reasons. The offer rides an ordinary reply
  // whose own words are the lead-in, exactly like `outreachPreviewCard`. The RESPONSE row is the
  // tempting one and it would be a real bug: a bare reaction produces no agent turn (share-for-reaction
  // §8), so this row is the ONLY record that Dana reacted at all — strip it from the model's view and
  // the agent never learns it happened, on this turn or any later one. It is a caption in the sense
  // that no model wrote it, but it is not a caption ABOUT the model's own actions, which is what this
  // predicate exists to hide. (Clients still treat it as server-written for their mid-turn drop
  // guards — a different question, answered by their own `hasPresentationCard`.)
  return Boolean((metadata == null ? void 0 : metadata.agentCard) || (metadata == null ? void 0 : metadata.agentAtWorkCard) || (metadata == null ? void 0 : metadata.milestoneCard) || (metadata == null ? void 0 : metadata.creditGiftCard) || (metadata == null || (_metadata$shopifyBatc = metadata.shopifyBatchCard) == null ? void 0 : _metadata$shopifyBatc.pushOnly));
}
const CREDIT_GIFT_KINDS = ['appreciation', 'reimbursement'];

/**
 * Read the credit-gift marker off a message's metadata, returning a validated
 * {@link CreditGiftCardMetadata} or `null` when the row is not a gift row. Shared by every client
 * renderer so the check stays identical across web and the native bridges.
 *
 * Guards `credits` hard — a positive, finite integer or nothing. This card tells a user how many
 * credits they were given, so a malformed marker rendering as "You received 0 credits", "NaN
 * credits" or a fractional count is a specific, embarrassing failure that a lenient parse would
 * produce and a strict one simply cannot. Rendering nothing is always the better fallback: the
 * agent's own reply already said it in words.
 */
export function readCreditGiftCard(metadata) {
  const card = metadata == null ? void 0 : metadata.creditGiftCard;
  if (!card || !CREDIT_GIFT_KINDS.includes(card.kind)) {
    return null;
  }
  const credits = typeof card.credits === 'number' && Number.isSafeInteger(card.credits) && card.credits > 0 ? card.credits : null;
  if (credits === null) {
    return null;
  }
  return {
    kind: card.kind,
    credits,
    ...(typeof card.note === 'string' && card.note.trim() ? {
      note: card.note.trim()
    } : {})
  };
}

/**
 * Read the connector-card marker off a message's metadata, returning a validated
 * {@link ConnectorCardMetadata} or `null` when the reply carries none. Shared by every client
 * renderer so the "does this reply carry a connect card?" check stays identical across web and the
 * native bridges. A card missing its required fields (toolkit/name/connectUrl) renders nothing
 * rather than a broken card, and `connectUrl` must be https so a corrupted marker can't become a
 * tappable non-web URL.
 */
export function readConnectorCard(metadata) {
  const card = metadata == null ? void 0 : metadata.connectorCard;
  if (!card || typeof card.toolkit !== 'string' || !card.toolkit.trim() || typeof card.name !== 'string' || !card.name.trim() || typeof card.connectUrl !== 'string' || !/^https:\/\/\S+$/i.test(card.connectUrl)) {
    return null;
  }
  return {
    toolkit: card.toolkit,
    name: card.name,
    connectUrl: card.connectUrl,
    ...(typeof card.logoUrl === 'string' && /^https?:\/\/\S+$/i.test(card.logoUrl) ? {
      logoUrl: card.logoUrl
    } : {}),
    ...(typeof card.mintedAt === 'string' && card.mintedAt.trim() ? {
      mintedAt: card.mintedAt
    } : {}),
    ...(typeof card.expiresAt === 'string' && card.expiresAt.trim() ? {
      expiresAt: card.expiresAt
    } : {}),
    ...(typeof card.source === 'string' && /^[a-z0-9-]{1,40}$/.test(card.source) ? {
      source: card.source
    } : {}),
    ...(card.reconnect === true ? {
      reconnect: true
    } : {})
  };
}
const PROFILE_LINK_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const PROFILE_LINK_HANDLE = /^[a-z0-9](?:[a-z0-9._-]*[a-z0-9])?$/;

/**
 * Read the profile-link card off a message's metadata, or `null` when the reply carries none or any required field is
 * malformed — a broken marker renders nothing rather than a card whose Publish acts on the wrong thing. Shared by every
 * client renderer (web imports it; the native readers mirror it). `linkId` must be a GUID, `url` https, `handle` a
 * handle's shape and `status` one of {@link PROFILE_LINK_CARD_STATUSES}; optional fields that are malformed are dropped.
 */
export function readProfileLinkCard(metadata) {
  const card = metadata == null ? void 0 : metadata.profileLinkCard;
  if (!card || typeof card !== 'object' || typeof card.linkId !== 'string' || !PROFILE_LINK_ID.test(card.linkId) || typeof card.url !== 'string' || !/^https:\/\/\S+$/i.test(card.url) || typeof card.handle !== 'string' || !PROFILE_LINK_HANDLE.test(card.handle) || !PROFILE_LINK_CARD_STATUSES.includes(card.status) || typeof card.mintedAt !== 'string' || Number.isNaN(Date.parse(card.mintedAt))) {
    return null;
  }
  return {
    linkId: card.linkId,
    url: card.url,
    handle: card.handle,
    ...(typeof card.contextTitle === 'string' && card.contextTitle.trim() ? {
      contextTitle: card.contextTitle
    } : {}),
    ...(typeof card.headline === 'string' && card.headline.trim() ? {
      headline: card.headline
    } : {}),
    status: card.status,
    ...(card.hasUnpublishedChanges === true ? {
      hasUnpublishedChanges: true
    } : {}),
    mintedAt: card.mintedAt
  };
}
/**
 * Every {@link ChipFamily} id, business families first. This is the ONE authoritative list in code —
 * the authoring side deliberately does not repeat it: the `using-goals` skill enumerates the families
 * for the model in prose (with what each covers, which an enum cannot carry), and an id that never
 * appears here is dropped at render by {@link readOpportunityCard}. Exported for any consumer that
 * needs to enumerate or validate the set rather than declaring its own copy.
 */
export const CHIP_FAMILY_IDS = ['revenue', 'new-clients', 'retention', 'client-results', 'get-paid', 'staffing', 'time-saved', 'reputation', 'launch', 'wellbeing', 'habits', 'learning', 'relationships', 'money', 'in-control', 'experiences'];

/**
 * Read the opportunity-card marker off a message's metadata, returning a validated
 * {@link OpportunityCardMetadata} or `null` when the reply carries none. Shared by every client
 * renderer AND by the harness that stamps it, so "does this reply carry an opportunity widget?" stays
 * one answer.
 *
 * Requires both `slug` and `title`. A card without a slug has CTAs that cannot resolve to a goal, and
 * one without a title has nothing to say — either way the assistant's own reply already made the
 * suggestion in words, so rendering nothing is strictly better than rendering a widget that misleads
 * or does nothing when tapped.
 *
 * An unrecognized `value` drops just that field. The chip is decoration on top of a card that still
 * reads correctly without it, and a forward chip family from a newer server must not blank the whole
 * widget — the opposite trade-off from the two required fields above.
 */
export function readOpportunityCard(metadata) {
  const card = metadata == null ? void 0 : metadata.opportunityCard;
  if (!card || typeof card.slug !== 'string' || !card.slug.trim() || typeof card.title !== 'string' || !card.title.trim()) {
    return null;
  }
  return {
    slug: card.slug.trim(),
    title: card.title.trim(),
    ...(typeof card.value === 'string' && CHIP_FAMILY_IDS.includes(card.value) ? {
      value: card.value
    } : {}),
    ...(typeof card.how === 'string' && card.how.trim() ? {
      how: card.how.trim()
    } : {})
  };
}
const INSIGHT_UNITS = ['money', 'count', 'percent'];
const isFiniteNumber = value => typeof value === 'number' && Number.isFinite(value);
const readInsightItem = raw => {
  const item = raw;
  if (!item || typeof item.id !== 'string' || typeof item.agentRole !== 'string') {
    return null;
  }
  if (!KNOWN_INSIGHT_KINDS.includes(item.kind) || !INSIGHT_UNITS.includes(item.unit) || !isFiniteNumber(item.value)) {
    return null;
  }
  return {
    id: item.id,
    kind: item.kind,
    value: item.value,
    unit: item.unit,
    agentRole: item.agentRole,
    ...(typeof item.currency === 'string' && /^[A-Z]{3}$/.test(item.currency) ? {
      currency: item.currency
    } : {}),
    ...(isFiniteNumber(item.count) ? {
      count: item.count
    } : {}),
    ...(item.capped === true ? {
      capped: true
    } : {}),
    ...(isFiniteNumber(item.windowDays) ? {
      windowDays: item.windowDays
    } : {}),
    ...(item.kind === 'rebookingFollowUp' && Number.isSafeInteger(item.minimumAgeDays) && item.minimumAgeDays >= 1 && item.minimumAgeDays <= 366 && isFiniteNumber(item.windowDays) && item.minimumAgeDays <= item.windowDays ? {
      minimumAgeDays: item.minimumAgeDays
    } : {})
  };
};

/**
 * Read `metadata.insightCard`. An item with an unknown kind, unit or a non-number value is dropped,
 * so a newer server cannot blank the card; a card with no item left, or no `computedAt`, is null.
 */
export function readInsightCard(metadata) {
  const card = metadata == null ? void 0 : metadata.insightCard;
  if (!card || typeof card.computedAt !== 'string' || Number.isNaN(Date.parse(card.computedAt)) || !Array.isArray(card.items)) {
    return null;
  }
  const items = card.items.flatMap(raw => readInsightItem(raw) ?? []).slice(0, MAX_INSIGHT_CARD_ITEMS);
  return items.length > 0 ? {
    computedAt: card.computedAt,
    items
  } : null;
}
function trimmedString(value) {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

/**
 * A product link a card may open and its buy line may carry: an http(s) URL with a host, at most 2048 characters,
 * with no whitespace, no backslash and no invisible (control or format) characters. Anything else is dropped, never repaired. The
 * same gate runs in the TS and Java harnesses and on iOS and Android, so all three clients carry the same link.
 */
export function safeProductUrl(value) {
  // The host must follow `//` in the text itself, and no backslash may appear: the WHATWG parser reads `https:///path`
  // as host `path` and a `\` as `/`, which the other clients' parsers do not, so they would disagree about the link.
  if (typeof value !== 'string' || value.length > 2048 || /[\s\p{Cc}\p{Cf}\\]/u.test(value) || !/^https?:\/\/[^/?#]/i.test(value)) {
    return undefined;
  }
  try {
    const url = new URL(value);
    return (url.protocol === 'http:' || url.protocol === 'https:') && url.hostname ? value : undefined;
  } catch {
    return undefined;
  }
}
function readProductCardItem(raw) {
  const item = raw;
  const attachmentUrl = typeof (item == null ? void 0 : item.attachmentUrl) === 'string' && item.attachmentUrl ? item.attachmentUrl : undefined;
  const title = trimmedString(item == null ? void 0 : item.title);
  if (!attachmentUrl || !title) {
    return null;
  }
  const price = trimmedString(item == null ? void 0 : item.price);
  const store = trimmedString(item == null ? void 0 : item.store);
  const productUrl = safeProductUrl(item == null ? void 0 : item.productUrl);
  const rating = item == null ? void 0 : item.rating;
  const reviews = item == null ? void 0 : item.reviews;
  return {
    attachmentUrl,
    title,
    ...(price ? {
      price
    } : {}),
    ...(store ? {
      store
    } : {}),
    ...(isFiniteNumber(rating) ? {
      rating
    } : {}),
    ...(isFiniteNumber(reviews) ? {
      reviews
    } : {}),
    ...(productUrl ? {
      productUrl
    } : {}),
    ...((item == null ? void 0 : item.kind) === 'place' ? {
      kind: 'place'
    } : {})
  };
}

/** Read `metadata.productCards`. A malformed item is dropped; a card with no item left is null. */
export function readProductCards(metadata) {
  const raw = metadata == null ? void 0 : metadata.productCards;
  if (!raw || !Array.isArray(raw.items)) {
    return null;
  }
  const items = raw.items.flatMap(item => readProductCardItem(item) ?? []);
  return items.length > 0 ? {
    items
  } : null;
}
const BAND_CARD_APP_STATUSES = ['connected', 'skipped', 'failed', 'pending'];
function readBandCardAgents(raw) {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.flatMap(agent => {
    const name = typeof (agent == null ? void 0 : agent.name) === 'string' ? agent.name.trim() : '';
    const title = typeof (agent == null ? void 0 : agent.title) === 'string' ? agent.title.trim() : '';
    const value = typeof (agent == null ? void 0 : agent.value) === 'string' ? agent.value.trim() : '';
    const id = typeof (agent == null ? void 0 : agent.id) === 'string' ? agent.id.trim() : '';
    return name ? [{
      name,
      title,
      ...(value ? {
        value
      } : {}),
      ...(id ? {
        id
      } : {})
    }] : [];
  });
}
function readBandCardApps(raw) {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.flatMap(app => {
    const toolkit = typeof (app == null ? void 0 : app.toolkit) === 'string' ? app.toolkit.trim() : '';
    const name = typeof (app == null ? void 0 : app.name) === 'string' ? app.name.trim() : '';
    if (!toolkit || !name) {
      return [];
    }
    return [{
      toolkit,
      name,
      ...(typeof app.logoUrl === 'string' && /^https?:\/\/\S+$/i.test(app.logoUrl) ? {
        logoUrl: app.logoUrl
      } : {}),
      ...(typeof app.status === 'string' && BAND_CARD_APP_STATUSES.includes(app.status) ? {
        status: app.status
      } : {}),
      ...(typeof app.group === 'string' && app.group.trim() ? {
        group: app.group.trim()
      } : {})
    }];
  });
}

/**
 * Read the band offer off a message's metadata, or `null` when the row carries none. Fails closed on an
 * offer with no usable option; an option needs a name and at least one agent, since a team with nobody in
 * it is not a band. Everything else is dropped item by item rather than failing the card.
 */
export function readBandOfferCard(metadata) {
  const card = metadata == null ? void 0 : metadata.bandOfferCard;
  if (!card || !Array.isArray(card.options)) {
    return null;
  }
  const options = card.options.flatMap((option, index) => {
    const name = typeof (option == null ? void 0 : option.name) === 'string' ? option.name.trim() : '';
    const agents = readBandCardAgents(option == null ? void 0 : option.agents);
    if (!name || agents.length === 0) {
      return [];
    }
    // http(s) only, the guard `logoUrl` already takes: a tap on this option OPENS this URL, so a forged
    // `javascript:` or `data:` here would run as a link the owner was told to tap.
    const connectUrl = optionalString(option.connectUrl);
    return [{
      id: typeof option.id === 'string' && option.id.trim() ? option.id.trim() : String(index + 1),
      name,
      tagline: typeof option.tagline === 'string' ? option.tagline.trim() : '',
      agents,
      apps: readBandCardApps(option.apps),
      ...(optionalString(option.goal) ? {
        goal: optionalString(option.goal)
      } : {}),
      ...(connectUrl && /^https?:\/\/\S+$/i.test(connectUrl) ? {
        connectUrl
      } : {}),
      ...(option.fromWixPack === true ? {
        fromWixPack: true
      } : {}),
      ...(option.fromWixPack === true && optionalString(option.businessName) ? {
        businessName: optionalString(option.businessName)
      } : {})
    }];
  });
  return options.length > 0 ? {
    options
  } : null;
}

/** Read the band card off a message's metadata, or `null`. Same floor as an offer option: a name and a team. */
export function readBandCard(metadata) {
  const card = metadata == null ? void 0 : metadata.bandCard;
  const name = typeof (card == null ? void 0 : card.name) === 'string' ? card.name.trim() : '';
  const agents = readBandCardAgents(card == null ? void 0 : card.agents);
  if (!card || !name || agents.length === 0) {
    return null;
  }
  const homeWidgets = Array.isArray(card.homeWidgets) ? card.homeWidgets.filter(nonEmptyString).map(slug => slug.trim()) : [];
  const connectUrl = optionalString(card.connectUrl);
  const goal = readBandCardGoal(card.goal);
  return {
    name,
    agents,
    apps: readBandCardApps(card.apps),
    ...(goal ? {
      goal
    } : {}),
    ...(homeWidgets.length > 0 ? {
      homeWidgets
    } : {}),
    ...(connectUrl ? {
      connectUrl
    } : {})
  };
}
const BAND_CARD_TASK_STATUSES = ['idle', 'working', 'done'];
function readBandCardGoal(value) {
  const goal = value;
  const title = optionalString(goal == null ? void 0 : goal.title);
  if (!title) {
    return undefined;
  }
  const tasks = (Array.isArray(goal == null ? void 0 : goal.tasks) ? goal.tasks : []).map(task => {
    const taskTitle = optionalString(task == null ? void 0 : task.title);
    if (!taskTitle) {
      return null;
    }
    const raw = task.status;
    // An unknown status reads as idle rather than dropping the row: a forward value from a newer
    // server must not make a task the user is being asked to approve disappear from the plan.
    const status = BAND_CARD_TASK_STATUSES.includes(raw) ? raw : 'idle';
    const agent = optionalString(task.agent);
    return {
      title: taskTitle,
      status,
      ...(agent ? {
        agent
      } : {})
    };
  }).filter(task => task !== null);
  const slug = optionalString(goal == null ? void 0 : goal.slug);
  const id = optionalString(goal == null ? void 0 : goal.id);
  const goalValue = optionalString(goal == null ? void 0 : goal.value);
  return {
    title,
    ...(slug ? {
      slug
    } : {}),
    ...(id ? {
      id
    } : {}),
    ...(goalValue ? {
      value: goalValue
    } : {}),
    ...(tasks.length > 0 ? {
      tasks
    } : {})
  };
}
const GOAL_CARD_VARIANTS = ['plan', 'completed'];

/** Read the goal card off a message's metadata, or `null`. The floor: a known variant, a title, and a way to find the goal. */
export function readGoalCard(metadata) {
  const card = metadata == null ? void 0 : metadata.goalCard;
  const variant = card == null ? void 0 : card.variant;
  if (!card || !GOAL_CARD_VARIANTS.includes(variant)) {
    return null;
  }
  const plan = readBandCardGoal({
    title: card.title,
    slug: card.slug,
    id: card.goalId,
    value: card.value,
    tasks: card.tasks
  });
  if (!plan || !plan.slug && !plan.id) {
    return null;
  }
  return {
    variant: variant,
    title: plan.title,
    ...(plan.slug ? {
      slug: plan.slug
    } : {}),
    ...(plan.id ? {
      goalId: plan.id
    } : {}),
    ...(plan.value ? {
      value: plan.value
    } : {}),
    ...(plan.tasks ? {
      tasks: plan.tasks
    } : {})
  };
}

/** The `plan` goal card a band card implies: its goal, when it has one the client can find. */
export function goalCardFromBandCard(bandCard) {
  var _goal$tasks;
  const goal = bandCard == null ? void 0 : bandCard.goal;
  if (!goal || !goal.slug && !goal.id) {
    return undefined;
  }
  return {
    variant: 'plan',
    title: goal.title,
    ...(goal.slug ? {
      slug: goal.slug
    } : {}),
    ...(goal.id ? {
      goalId: goal.id
    } : {}),
    ...(goal.value ? {
      value: goal.value
    } : {}),
    ...((_goal$tasks = goal.tasks) != null && _goal$tasks.length ? {
      tasks: goal.tasks
    } : {})
  };
}
export const OUTREACH_PREVIEW_CHANNELS = ['whatsapp', 'sms', 'email', 'gmail', 'app'];
const OUTREACH_PREVIEW_DELIVERIES = ['verbatim', 'wrapped', 'contextual'];
const nonEmptyString = value => typeof value === 'string' && value.trim().length > 0;
const optionalString = value => nonEmptyString(value) ? value.trim() : undefined;
// The template frame meets the body edge to edge, so its own spacing is part of the text and is kept.
const verbatimString = value => nonEmptyString(value) ? value : undefined;
const stringList = value => {
  const items = Array.isArray(value) ? value.filter(nonEmptyString).map(item => item.trim()) : [];
  return items.length > 0 ? items : undefined;
};
function readOutreachPreviewEntry(value) {
  const entry = value;
  if (!entry || !nonEmptyString(entry.name) || typeof entry.body !== 'string' || !nonEmptyString(entry.stateLine)) {
    return null;
  }
  // An unrecognized delivery falls back to verbatim rather than dropping the entry: the words are still
  // the words, and a forward value from a newer server must not blank a preview the user has to approve.
  const delivery = OUTREACH_PREVIEW_DELIVERIES.includes(entry.delivery) ? entry.delivery : 'verbatim';
  const optional = {
    relation: optionalString(entry.relation),
    address: optionalString(entry.address),
    frameBefore: verbatimString(entry.frameBefore),
    frameAfter: verbatimString(entry.frameAfter),
    leadTemplate: verbatimString(entry.leadTemplate),
    followsLabel: optionalString(entry.followsLabel),
    subject: optionalString(entry.subject),
    from: optionalString(entry.from),
    files: stringList(entry.files),
    footnote: optionalString(entry.footnote),
    personId: optionalString(entry.personId)
  };
  return {
    name: entry.name.trim(),
    stateLine: entry.stateLine.trim(),
    delivery,
    body: entry.body,
    ...(entry.symphonyUser === true ? {
      symphonyUser: true
    } : {}),
    ...Object.fromEntries(Object.entries(optional).filter(_ref => {
      let v = _ref[1];
      return v !== undefined;
    }))
  };
}

/**
 * Read the outreach-preview marker off a message's metadata, returning a validated
 * {@link OutreachPreviewCardMetadata} or `null` when the reply carries none. Shared by every client
 * renderer AND by the harness that stamps it, so "does this reply carry a preview?" stays one answer.
 *
 * A group whose channel is unknown, or that has no readable entry, is dropped; a card left with no
 * group renders nothing. Nothing is invented for a missing caption: the labels default to English,
 * because a caption in the wrong language is a smaller failure than a card with no captions, and the
 * server always sends them anyway.
 */
export function readOutreachPreviewCard(metadata) {
  const card = metadata == null ? void 0 : metadata.outreachPreviewCard;
  if (!card || !Array.isArray(card.groups)) {
    return null;
  }
  const groups = [];
  for (const group of card.groups) {
    if (!group || !OUTREACH_PREVIEW_CHANNELS.includes(group.channel) || !Array.isArray(group.entries)) {
      continue;
    }
    const entries = group.entries.map(readOutreachPreviewEntry).filter(entry => entry !== null);
    if (entries.length === 0) {
      continue;
    }
    groups.push({
      channel: group.channel,
      channelLabel: optionalString(group.channelLabel) ?? group.channel,
      entries
    });
  }
  if (groups.length === 0) {
    return null;
  }
  const labels = card.labels ?? {};
  return {
    groups,
    labels: {
      badge: optionalString(labels.badge) ?? 'Symphony user',
      subject: optionalString(labels.subject) ?? 'Subject',
      from: optionalString(labels.from) ?? 'From',
      files: optionalString(labels.files) ?? 'Attached'
    },
    actions: stringList(card.actions) ?? []
  };
}
const SOCIAL_POST_PREVIEW_MEDIA_KINDS = ['image', 'video'];
const httpsUrl = value => {
  const url = safeProductUrl(value);
  return url && /^https:/i.test(url) ? url : undefined;
};
export function readSocialPostPreviewCard(metadata) {
  const card = metadata == null ? void 0 : metadata.socialPostPreviewCard;
  if (!card || typeof card !== 'object') {
    return null;
  }
  const network = optionalString(card.network);
  const decisionId = optionalString(card.decisionId);
  if (!network || !decisionId) {
    return null;
  }
  const media = [];
  for (const item of Array.isArray(card.media) ? card.media : []) {
    const url = httpsUrl(item == null ? void 0 : item.url);
    if (url && SOCIAL_POST_PREVIEW_MEDIA_KINDS.includes(item == null ? void 0 : item.kind) && media.length < MAX_SOCIAL_POST_PREVIEW_MEDIA) {
      media.push({
        url,
        kind: item.kind
      });
    }
  }
  const caption = typeof card.caption === 'string' && card.caption.trim() ? card.caption : undefined;
  const declaredCount = typeof card.mediaCount === 'number' && Number.isFinite(card.mediaCount) ? Math.floor(card.mediaCount) : 0;
  const mediaCount = Math.max(media.length, declaredCount);
  if (!caption && mediaCount === 0 && !httpsUrl(card.link)) {
    return null;
  }
  const labels = card.labels ?? {};
  const mediaNotShown = optionalString(labels.mediaNotShown);
  const postTypeLabel = optionalString(labels.postType);
  const expires = optionalString(labels.expires);
  const accountLabel = optionalString(card.accountLabel);
  const accountName = optionalString(card.accountName);
  const accountPictureUrl = httpsUrl(card.accountPictureUrl);
  const link = httpsUrl(card.link);
  const scheduledAt = optionalString(card.scheduledAt);
  const scheduledText = optionalString(card.scheduledText);
  const postType = SOCIAL_POST_TYPES.find(type => type === card.postType);
  return {
    network,
    networkLabel: optionalString(card.networkLabel) ?? network,
    ...(postType ? {
      postType
    } : {}),
    ...(accountLabel ? {
      accountLabel
    } : {}),
    ...(accountName ? {
      accountName
    } : {}),
    ...(accountPictureUrl ? {
      accountPictureUrl
    } : {}),
    ...(caption ? {
      caption
    } : {}),
    media,
    mediaCount,
    ...(link ? {
      link
    } : {}),
    ...(scheduledAt && !Number.isNaN(Date.parse(scheduledAt)) ? {
      scheduledAt
    } : {}),
    ...(scheduledText ? {
      scheduledText
    } : {}),
    ...(card.expiresWithinDay === true ? {
      expiresWithinDay: true
    } : {}),
    decisionId,
    taskId: optionalString(card.taskId) ?? '',
    labels: {
      badge: optionalString(labels.badge) ?? 'Preview',
      postsNow: optionalString(labels.postsNow) ?? 'Goes out as soon as you approve it',
      scheduled: optionalString(labels.scheduled) ?? 'Scheduled for',
      textOnly: optionalString(labels.textOnly) ?? 'Text only',
      seeMore: optionalString(labels.seeMore) ?? 'See more',
      seeLess: optionalString(labels.seeLess) ?? 'See less',
      ...(postTypeLabel ? {
        postType: postTypeLabel
      } : {}),
      ...(mediaNotShown ? {
        mediaNotShown
      } : {}),
      ...(expires ? {
        expires
      } : {})
    }
  };
}

/**
 * The share offer's chips are the reply's ORDINARY {@link MessageChoices}, and these four functions are
 * the whole coupling between a tap and what it authorizes.
 *
 * Why the chips are choices rather than buttons drawn inside {@link ShareCardOfferCardMetadata}: a
 * metadata key a shipped build has never heard of reads as absent, so a card whose send lived inside
 * the new component could not be sent at all on an app already in the field. `MessageChoices` has been
 * understood by every build since 2026-07-25, so an old app renders the same names, a tap still
 * resolves to the same send, and the card is purely the picture it could not draw. Same shape as the
 * outreach preview, whose Send/Edit are choices for the same reason: **the card renders no buttons.**
 *
 * The person id rides the option ID, never the label. A label is display copy — it is translated, it is
 * rewritten by designers, and on an old build it is the only thing the user sees — so resolving a tap
 * by parsing it would make the send depend on the wording of a button.
 *
 * ⚠️ **The send label must SAY that a tap sends.** "Show Dana · via WhatsApp" reads as plausibly
 * "preview Dana's version" as "send it to Dana now", and §7.1 gives the user no confirm step to
 * discover the difference in. On a current client `labels.chipsHint` says it — but that string lives
 * inside {@link ShareCardOfferCardMetadata}, which is exactly the thing an app that predates this key
 * cannot draw, so on every build in the field the label is the ONLY cue that survives. Whoever mints
 * these options therefore writes the send into the label itself ("Send to Dana · WhatsApp"), and the
 * reply's own prose says the tap sends and that there are a few seconds to take it back. Both are
 * server-authored display copy, and neither is ever parsed.
 */
export const SHARE_OFFER_ASK_PREFIX = 'share-offer:';
export const SHARE_SEND_OPTION_PREFIX = 'share-send:';
export const SHARE_UNDO_OPTION_PREFIX = 'share-undo:';
/**
 * The answer a client sends from "Send to someone else", with the chosen person in
 * `ChoiceAnswer.recipient`. A fixed id rather than a prefix: there is no person to name in it, because
 * the person is exactly what the user is about to choose.
 */
export const SHARE_SEND_OTHER_OPTION_ID = 'share-send-other';
/**
 * The capability a client advertises when it can draw "Send to someone else".
 *
 * An offer that has NO chips is only worth drawing when the user can send it to someone else — an
 * older build draws such a card with nothing on it to tap. So the harness draws chipless cards only
 * for a turn whose USER row says the client that sent it knows this. See `metadata.clientCapabilities`.
 */
export const CLIENT_CAPABILITY_SHARE_SOMEONE_ELSE = 'share-someone-else';

/**
 * The capabilities a client advertises when it draws `inviteCard` / `packShareCard`. Both cards spend a one-time
 * ask when attached, so the harness attaches them only to a turn whose USER row says its client can draw them.
 */
export const CLIENT_CAPABILITY_INVITE_CARD = 'invite-card';
export const CLIENT_CAPABILITY_PACK_SHARE_CARD = 'pack-share-card';

/**
 * The capability a client advertises when it opens {@link MessageChoiceOption.url} link chips. Sent in the
 * body of the request that writes the chip (a meeting's event thread), since that row is written outside
 * any turn and there is no USER row to read it from.
 */
export const CLIENT_CAPABILITY_LINK_CHIPS = 'link-chips';
const idAfter = (value, prefix) => typeof value === 'string' && value.startsWith(prefix) && value.slice(prefix.length).trim().length > 0 ? value.slice(prefix.length).trim() : null;
export const shareOfferAskId = offerId => `${SHARE_OFFER_ASK_PREFIX}${offerId}`;
export const offerIdFromShareOfferAskId = askId => idAfter(askId, SHARE_OFFER_ASK_PREFIX);
export const shareSendOptionId = personId => `${SHARE_SEND_OPTION_PREFIX}${personId}`;
/** A foreign option id answers `null` rather than a bogus person: a tap it cannot read is not a send. */
export const personIdFromShareSendOptionId = optionId => idAfter(optionId, SHARE_SEND_OPTION_PREFIX);
export const shareUndoOptionId = cardId => `${SHARE_UNDO_OPTION_PREFIX}${cardId}`;
export const cardIdFromShareUndoOptionId = optionId => idAfter(optionId, SHARE_UNDO_OPTION_PREFIX);

/**
 * A held task's own question, asked IN THE CHAT, where the person actually is.
 *
 * ## The number this exists for
 *
 * A gate parks work on a task card with a Run it chip under it; the maestro relays "there is a Run it
 * button on it"; and the reader — who is in the conversation, not on the task screen — answers there.
 * Measured on the attended arm over its first four days: **527 holds, 12 approvals ever spent, 2.3%**.
 * `ManageDecision.relay_answer` was the first answer to that and is a model-mediated one: the agent has
 * to notice the reply, pick the right question and quote the user correctly, and every turn it does not
 * is a user who answered into a channel that recorded nothing.
 *
 * So the chips move to where the question was asked. They are ORDINARY {@link MessageChoices} — the
 * same mechanism the share offer and the outreach preview send on — which is the whole reason this is a
 * pair of id prefixes and not a card: every client in the field already renders them, already sends a
 * {@link ChoiceAnswer} back, and needs no build to take part.
 *
 * ## The id carries the binding, never the label
 *
 * Same rule the share and meeting-brief chips state: the label is display copy in the user's language,
 * and the id is what the harness spends the tap on. The ask names the DECISION and the option names the
 * decision's own choice token — `run-it`, `drop` — so a tap resolves to one verdict on one question
 * with nothing inferred from prose. The decision id is repeated inside the option id deliberately: a
 * client that echoes an `optionId` without its `askId` still identifies the question completely, and a
 * chip from an older reply cannot be spent against a newer ask.
 *
 * ⚠️ **A tap here SETTLES a question that may release an irreversible act.** That is the point — it is
 * the same authority as the chip on the task card — so whoever mints these writes the consequence into
 * the label ("Run it"), exactly as the share chips must say that a tap sends.
 */
export const DECISION_ASK_PREFIX = 'decision:';
/**
 * Short, and the shortness is a CONSTRAINT rather than a style.
 *
 * `sanitizeChoiceAnswer` drops any `optionId` over {@link CHOICE_ANSWER_ID_MAX_LENGTH}, silently and
 * before the tap reaches anything — so an option id that overruns renders a chip the user can press and
 * that does nothing at all, which is the exact defect this whole feature exists to delete. A decision id
 * is a 36-character uuid and the two colons are fixed, so the prefix and the choice token share what is
 * left. At `decision-answer:` that was 11 characters for the token, and two real offers already exceed
 * it (`retry-review`, `credits-approved`).
 */
export const DECISION_ANSWER_OPTION_PREFIX = 'da:';

/**
 * The ceiling `sanitizeChoiceAnswer` enforces on both ids of a `ChoiceAnswer`.
 *
 * Here rather than in the harness's validator because it is a WIRE fact that the minting side has to
 * respect: whoever builds an option id has to know what the reading side will refuse, or the two drift
 * and the drift is invisible — a chip that renders and cannot be spent. The validator imports it.
 */
export const CHOICE_ANSWER_ID_MAX_LENGTH = 64;
export const decisionAskId = decisionId => `${DECISION_ASK_PREFIX}${decisionId}`;
export const decisionIdFromDecisionAskId = askId => idAfter(askId, DECISION_ASK_PREFIX);

/**
 * `decision-answer:<decisionId>:<choiceToken>`.
 *
 * A decision id is a service-minted uuid and a choice token is an option id the offer author chose
 * (`run-it`, `drop`, `approve`), so neither half carries a colon today — but "today" is not a contract,
 * so the SPLIT is on the FIRST colon and the token keeps everything after it. That way a token that
 * grows one stays readable, and the half that must never be ambiguous — which question — is the half
 * the split protects.
 */
export const decisionAnswerOptionId = (decisionId, choiceToken) => `${DECISION_ANSWER_OPTION_PREFIX}${decisionId}:${choiceToken}`;

/** A foreign or malformed option id answers `null` rather than a guess: a tap we cannot read is not an answer. */
export const readDecisionAnswerOptionId = optionId => {
  const rest = idAfter(optionId, DECISION_ANSWER_OPTION_PREFIX);
  if (!rest) {
    return null;
  }
  const split = rest.indexOf(':');
  if (split <= 0) {
    return null;
  }
  const decisionId = rest.slice(0, split).trim();
  const choiceToken = rest.slice(split + 1).trim();
  return decisionId && choiceToken ? {
    decisionId,
    choiceToken
  } : null;
};

/**
 * What the harness stamps on the SYSTEM notice that tells a maestro a task is holding, so the reply it
 * writes can carry the question's own chips.
 *
 * It rides the NOTICE rather than being re-derived at reply time on purpose: the notice is written by
 * the park that raised the question, which is the one place that knows the decision id and the offer's
 * choices together. Re-reading them later would mean a query per turn on the hottest loop in the
 * product, to recover a fact we already had.
 */

/**
 * The proposals a connector deep dive put to the owner, on the Maestro's reply that presents them.
 *
 * Names the decisions only. Their titles, reasons and live state come from `conversation-decisions`, so
 * a proposal answered elsewhere never renders open here, which is the same rule the meeting-approval
 * card follows.
 */

/** The most proposals one card lists. A sweep asks for three; the cap only bounds a malformed row. */
export const PROPOSALS_CARD_MAX = 10;

/** Read + sanitize `metadata.proposalsCard`: trimmed, de-duplicated ids, capped; none left means no card. */
export function readProposalsCard(metadata) {
  const raw = metadata == null ? void 0 : metadata.proposalsCard;
  const ids = raw == null ? void 0 : raw.decisionIds;
  if (!Array.isArray(ids)) {
    return undefined;
  }
  const decisionIds = [];
  for (const id of ids) {
    const clean = typeof id === 'string' ? id.trim() : '';
    if (clean && !decisionIds.includes(clean)) {
      decisionIds.push(clean);
    }
  }
  return decisionIds.length > 0 ? {
    decisionIds: decisionIds.slice(0, PROPOSALS_CARD_MAX)
  } : undefined;
}

/** Read + sanitize `metadata.decisionAsk`: a decision id and at least one well-formed option, else undefined. */
export function readDecisionAsk(metadata) {
  const raw = metadata == null ? void 0 : metadata.decisionAsk;
  if (!raw || typeof raw !== 'object') {
    return undefined;
  }
  const _ref2 = raw,
    decisionId = _ref2.decisionId,
    taskId = _ref2.taskId,
    options = _ref2.options;
  if (typeof decisionId !== 'string' || !decisionId.trim() || !Array.isArray(options)) {
    return undefined;
  }
  const clean = [];
  for (const option of options) {
    const _ref3 = option ?? {},
      id = _ref3.id,
      label = _ref3.label;
    if (typeof id === 'string' && id.trim() && typeof label === 'string' && label.trim()) {
      clean.push({
        id: id.trim(),
        label: label.trim()
      });
    }
  }
  return clean.length > 0 ? {
    decisionId: decisionId.trim(),
    ...(typeof taskId === 'string' && taskId.trim() ? {
      taskId: taskId.trim()
    } : {}),
    options: clean
  } : undefined;
}

/**
 * The "Set meeting brief" chip future meetings' conversations used to open with. No longer written, but
 * threads opened before still hold it, so its tap is still read (`meeting-brief-tap.ts`). The calendar event rides the option id, never the
 * label, for the reason the share chips give: the label is translated display copy, and the id is what
 * the harness spends the tap on (it reads that event's talking points into the turn before the model
 * answers).
 */
export const MEETING_BRIEF_ASK_PREFIX = 'meeting-brief-ask:';
export const MEETING_BRIEF_OPTION_PREFIX = 'meeting-brief:';
export const meetingBriefAskId = calendarEventId => `${MEETING_BRIEF_ASK_PREFIX}${calendarEventId}`;
export const meetingBriefOptionId = calendarEventId => `${MEETING_BRIEF_OPTION_PREFIX}${calendarEventId}`;
export const calendarEventIdFromMeetingBriefOptionId = optionId => idAfter(optionId, MEETING_BRIEF_OPTION_PREFIX);

/** The "Join this meeting" link chip a future meeting's conversation opens with. Never answered, so nothing reads the ids back. */
export const meetingJoinAskId = calendarEventId => `meeting-join-ask:${calendarEventId}`;
export const meetingJoinOptionId = calendarEventId => `meeting-join:${calendarEventId}`;
export const SHARE_CARD_KINDS = ['FILE', 'CHOICE', 'TASK'];
export const SHARE_CARD_CHANNELS = ['whatsapp', 'email'];
export const SHARE_CARD_CHIP_STATES = ['ready', 'sending', 'sent', 'undone', 'undo_refused', 'failed'];
export const SHARE_CARD_RESPONSE_KINDS = ['reaction', 'option', 'chip', 'reply'];

/** The window the clients count down when the server sends none. The service is still the authority. */
export const SHARE_CARD_UNDO_SECONDS = 10;
function readShareCardChip(value) {
  const chip = value;
  if (!chip || !nonEmptyString(chip.personId) || !nonEmptyString(chip.displayName) || !SHARE_CARD_CHANNELS.includes(chip.channel)) {
    return null;
  }
  // An unknown state falls back to `ready` rather than dropping the chip — except that a chip already
  // spent must never fall back to a tappable one, which is why `cardId` alone promotes it to `sent`:
  // re-offering a tap on a card that has gone out is the one wrong answer here.
  const known = SHARE_CARD_CHIP_STATES.includes(chip.state) ? chip.state : undefined;
  const state = known ?? (nonEmptyString(chip.cardId) ? 'sent' : 'ready');
  return {
    personId: chip.personId.trim(),
    displayName: chip.displayName.trim(),
    channel: chip.channel,
    channelLabel: optionalString(chip.channelLabel) ?? (chip.channel === 'whatsapp' ? 'via WhatsApp' : 'via email'),
    state,
    ...(optionalString(chip.cardId) ? {
      cardId: optionalString(chip.cardId)
    } : {}),
    ...(optionalString(chip.sentAt) ? {
      sentAt: optionalString(chip.sentAt)
    } : {})
  };
}
function readShareCardOptions(value) {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.flatMap(raw => {
    const option = raw;
    if (!option || !nonEmptyString(option.id) || !nonEmptyString(option.label)) {
      return [];
    }
    const mediaUrl = optionalString(option.mediaUrl);
    return [{
      id: option.id.trim(),
      label: option.label.trim(),
      ...(mediaUrl ? {
        mediaUrl
      } : {})
    }];
  });
}

/**
 * Read the share-offer marker off a message's metadata. Shared by every client renderer AND by the
 * harness that stamps it, so "does this reply carry an offer?" stays one answer.
 *
 * A card with no `offerId` or no `title` renders nothing: without the offer id a chip cannot authorize
 * anything, so there is no card to draw. Unreadable chips are dropped one by one, and a card left with
 * none becomes a PREVIEW rather than a card whose names silently disappeared.
 */
export function readShareOfferCard(metadata) {
  var _card$someoneElse;
  const card = metadata == null ? void 0 : metadata.shareOfferCard;
  if (!card || !nonEmptyString(card.offerId) || !nonEmptyString(card.title) || !SHARE_CARD_KINDS.includes(card.kind)) {
    return null;
  }
  const chips = (Array.isArray(card.chips) ? card.chips : []).map(readShareCardChip).filter(chip => chip !== null);
  const options = readShareCardOptions(card.options);
  const media = card.media;
  const mediaUrl = optionalString(media == null ? void 0 : media.url);
  const labels = card.labels ?? {};
  const undoSeconds = typeof card.undoSeconds === 'number' && card.undoSeconds > 0 ? Math.floor(card.undoSeconds) : SHARE_CARD_UNDO_SECONDS;
  // Kept only in its one valid shape. A reader that dropped it would silently remove "Send to someone
  // else" from every client that reads cards through here — the same failure as a sanitizer that keeps
  // only the fields it already knew about.
  const someoneElseOptionId = optionalString((_card$someoneElse = card.someoneElse) == null ? void 0 : _card$someoneElse.optionId);
  return {
    offerId: card.offerId.trim(),
    kind: card.kind,
    title: card.title.trim(),
    ...(optionalString(card.note) ? {
      note: optionalString(card.note)
    } : {}),
    ...(optionalString(card.body) ? {
      body: optionalString(card.body)
    } : {}),
    ...(mediaUrl ? {
      media: {
        url: mediaUrl,
        ...(optionalString(media == null ? void 0 : media.mimeType) ? {
          mimeType: optionalString(media == null ? void 0 : media.mimeType)
        } : {}),
        ...(optionalString(media == null ? void 0 : media.fileName) ? {
          fileName: optionalString(media == null ? void 0 : media.fileName)
        } : {}),
        ...(optionalString(media == null ? void 0 : media.posterUrl) ? {
          posterUrl: optionalString(media == null ? void 0 : media.posterUrl)
        } : {})
      }
    } : {}),
    ...(options.length > 0 ? {
      options
    } : {}),
    chips,
    // Preview is what the ABSENCE of chips means, whoever wrote the row: an older harness that sent no
    // flag and a redaction that cleared the names must render the same, because they are the same card.
    // ...EXCEPT when the user can still send it to someone they choose: a chipless card with that
    // control is the only card an account with nobody on file can get, and forcing it to preview here
    // would hand every client a card with nothing on it to tap.
    ...(card.previewOnly === true || chips.length === 0 && !someoneElseOptionId ? {
      previewOnly: true
    } : {}),
    ...(someoneElseOptionId ? {
      someoneElse: {
        optionId: someoneElseOptionId
      }
    } : {}),
    undoSeconds,
    labels: {
      sendingTo: optionalString(labels.sendingTo) ?? 'Sending to {name}…',
      undo: optionalString(labels.undo) ?? 'Undo',
      sentTo: optionalString(labels.sentTo) ?? 'Sent to {name}',
      undone: optionalString(labels.undone) ?? 'Taken back — it never went out',
      undoTooLate: optionalString(labels.undoTooLate) ?? 'Too late to take that one back; it had already gone',
      failed: optionalString(labels.failed) ?? 'It did not go out',
      options: optionalString(labels.options) ?? 'They pick one',
      chipsHint: optionalString(labels.chipsHint) ?? 'Tap a name to send it',
      previewOnly: optionalString(labels.previewOnly) ?? 'Have a look before this goes anywhere',
      // English fallbacks only where the control itself is present, so a card without it carries none.
      ...(someoneElseOptionId ? {
        someoneElse: optionalString(labels.someoneElse) ?? 'Send to someone else',
        emailPlaceholder: optionalString(labels.emailPlaceholder) ?? 'Their email',
        sendToEmail: optionalString(labels.sendToEmail) ?? 'Send',
        cancel: optionalString(labels.cancel) ?? 'Cancel',
        needsEmail: optionalString(labels.needsEmail) ?? 'That needs an email address'
      } : {})
    }
  };
}

/**
 * Read the response marker off a message's metadata.
 *
 * `line` is the row — a finished sentence written by the server — so a row without one renders
 * nothing rather than being assembled here out of parts: a client that stitched its own sentence
 * would say it differently from the push notification about the same reaction.
 */
export function readShareResponseCard(metadata) {
  const card = metadata == null ? void 0 : metadata.shareResponseCard;
  if (!card || !nonEmptyString(card.cardId) || !nonEmptyString(card.personName) || !nonEmptyString(card.line)) {
    return null;
  }
  const kind = SHARE_CARD_RESPONSE_KINDS.includes(card.kind) ? card.kind : 'reaction';
  const reaction = optionalString(card.reaction);
  const answer = optionalString(card.answer);
  const cardTitle = optionalString(card.cardTitle);
  return {
    cardId: card.cardId.trim(),
    personName: card.personName.trim(),
    kind,
    ...(reaction ? {
      reaction
    } : {}),
    ...(answer ? {
      answer
    } : {}),
    ...(cardTitle ? {
      cardTitle
    } : {}),
    line: card.line.trim()
  };
}

/** Contact info resolved from a [Name] tag in the user's message. */

/** Task info resolved from a [Label] tag in the user's message. */

/** Pulse template info resolved from a [Name] tag when the user picks a template from the catalog. */

/** Structured references attached by the mobile client when a user @-mentions contacts, tasks, or pulse templates. */

/** Shape accepted by create calls — only the fields the caller controls. */

const TEXT_ROLES = new Set([MessageRole.USER, MessageRole.ASSISTANT, MessageRole.ASSISTANT_STEP, MessageRole.SYSTEM, MessageRole.TOOL_RESULT, MessageRole.THINKING]);
export const isTextMessageRole = role => TEXT_ROLES.has(role);
export const isValidIsaConversationMessage = properties => {
  if (!isValidBase(properties) || !validateProps(properties, ['conversationId'])) {
    return false;
  }
  // Narrow to the loose shape for role-specific discriminated-union checks below.
  const p = properties;
  if (p.role === undefined || p.role === MessageRole.UNKNOWN_MESSAGE_ROLE) {
    return false;
  }
  if (p.role === MessageRole.TOOL_USE) {
    return typeof p.toolName === 'string' && p.toolName.length > 0 && typeof p.toolInput === 'string' && p.toolInput.length > 0;
  }
  if (isTextMessageRole(p.role)) {
    return typeof p.content === 'string';
  }
  return false;
};